'use client'

import { useMemo } from 'react'
import { SlidersHorizontalIcon, XIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'

import { Button } from '@/components/ui/button'
import { useEventsContext } from '@/providers/EventsProvider'
import { useLenisContext } from '@/providers/Lenis'
import { cn } from '@/utilities/ui'

import FilterInner from './FilterInner'

export const Filter: React.FC = () => {
  const { filterOpen, setFilterOpen } = useEventsContext()
  const { isMobile } = useLenisContext()

  const filterMotion = useMemo(() => {
    if (isMobile) {
      return {
        initial: filterOpen ? false : { opacity: 0, x: '100%' },
        animate: filterOpen ? { opacity: 1, x: 0 } : { opacity: 0, x: '100%' },
        exit: filterOpen ? { opacity: 0, x: '100%' } : { opacity: 1, x: 0 },
      }
    }

    return {
      initial: false,
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0 },
    }
  }, [filterOpen, isMobile])

  const handleFilterToggle = () => setFilterOpen(!filterOpen)

  return (
    <>
      {/* Mobile filter toggle button */}
      <Button
        variant="icon"
        className={cn(
          'fixed right-4 bottom-4 z-50 size-11 cursor-pointer border border-accent-2 bg-accent shadow-md shadow-primary/10 lg:hidden',
        )}
        onClick={handleFilterToggle}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={filterOpen ? 'close' : 'open'}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.1, ease: 'easeOut' }}
          >
            {filterOpen ? (
              <XIcon className="size-4.5" />
            ) : (
              <SlidersHorizontalIcon className="size-4.5" />
            )}
          </motion.div>
        </AnimatePresence>
      </Button>
      {/* Filter sidebar */}
      <motion.div
        initial={filterMotion.initial}
        animate={filterMotion.animate}
        exit={filterMotion.exit}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className={cn(
          'scrollbar-hidden relative z-30 flex flex-col items-start gap-4 overflow-y-auto max-lg:max-h-[calc(100vh-84px-4rem)]',
          'self-start rounded-xl border border-border bg-background p-4 max-lg:fixed max-lg:inset-x-4 max-lg:top-21 max-lg:z-20 max-lg:w-[calc(100%-32px)] max-lg:rounded-xl max-lg:border max-lg:border-border max-lg:bg-background max-lg:p-4 max-lg:shadow-md max-lg:shadow-primary/10 md:px-3',
          isMobile && !filterOpen && 'pointer-events-none',
        )}
      >
        <FilterInner />
      </motion.div>
      <motion.div
        className="fixed top-0 left-0 z-19 size-full bg-black/5 backdrop-blur-xs lg:hidden"
        initial={false}
        animate={
          filterOpen ? { opacity: 1, visibility: 'visible' } : { opacity: 0, visibility: 'hidden' }
        }
        transition={{ duration: 0.2, delay: filterOpen ? 0 : 0.1, ease: 'easeOut' }}
        onClick={() => setFilterOpen(false)}
      />
    </>
  )
}
